// Watches the account's connections for a sync in progress.
//
// A first backfill reads months of history, and until this existed the only
// thing the dashboard did about that was show the same empty numbers it had
// shown a second earlier. Tenants read that as a broken product — reasonably,
// because nothing on screen distinguished "still working" from "found nothing".
//
// So: poll the connection inventory (the API's own progress indicator, not a
// second read path), say out loud what the sync is doing, and refetch the
// screens as the rows land so the figures fill in rather than appearing all at
// once at the end.
import { useEffect, useRef, useState } from "react";
import { api, type Connection } from "../api";
import { revalidateAsync } from "../useAsync";

const IDLE_POLL_MS = 30_000;
const BUSY_POLL_MS = 5_000;

/**
 * How often the rest of the screen is refetched mid-sync. Every poll would be
 * far too eager — a refresh fans out to every mounted read — but waiting for
 * the run to finish is the problem we are fixing. Twenty seconds is often
 * enough to watch a funnel fill in, rarely enough to be felt.
 *
 * This must go through revalidateAsync, never bustAsyncCache: clearing the
 * cache drops every screen back to a skeleton, so on a timer the page reads as
 * permanently loading and the numbers we just worked to deliver are the one
 * thing nobody can see.
 */
const REFRESH_EVERY_MS = 20_000;

export type SyncWatch = {
  /** Connections mid-run right now. */
  syncing: Connection[];
  /** The most useful single sentence to show, or null when nothing is running. */
  phase: string | null;
};

export function useSyncWatch(): SyncWatch {
  const [syncing, setSyncing] = useState<Connection[]>([]);
  const wasSyncing = useRef(false);
  const lastRefresh = useRef(0);

  useEffect(() => {
    let live = true;
    let timer: number | undefined;

    const tick = async () => {
      let rows: Connection[];
      try {
        rows = await api.connectionsQuiet();
      } catch {
        // A failed poll is not worth surfacing: the screens have their own
        // error states, and this one is speculative by nature.
        if (live) timer = window.setTimeout(tick, IDLE_POLL_MS);
        return;
      }
      if (!live) return;

      const busy = rows.filter((c) => c.syncing);
      setSyncing(busy);

      const now = Date.now();
      const finished = wasSyncing.current && busy.length === 0;
      // Refetch when the run ends — that is the moment the numbers are final —
      // and periodically while it is still going, so history appears as it is
      // read instead of in one jump at the end.
      if (finished || (busy.length > 0 && now - lastRefresh.current > REFRESH_EVERY_MS)) {
        lastRefresh.current = now;
        revalidateAsync();
      }
      wasSyncing.current = busy.length > 0;

      timer = window.setTimeout(tick, busy.length > 0 ? BUSY_POLL_MS : IDLE_POLL_MS);
    };

    void tick();
    return () => {
      live = false;
      if (timer) window.clearTimeout(timer);
    };
  }, []);

  return { syncing, phase: phraseFor(syncing) };
}

const LABELS: Record<string, string> = {
  ga4: "Google Analytics",
  gtm: "Google Tag Manager",
  google_ads: "Google Ads",
  meta_ads: "Meta Ads",
  linkedin_ads: "LinkedIn Ads",
  meta_social: "Meta Social",
  x_social: "X",
  mysql: "MySQL",
  postgres: "Postgres",
  mongo: "MongoDB",
  posthog: "PostHog",
  semrush: "SEMrush",
  clarity: "Microsoft Clarity",
  gsc: "Search Console",
  bing: "Bing Webmaster",
  ahrefs: "Ahrefs",
  opinly: "Opinly",
};

function phraseFor(syncing: Connection[]): string | null {
  if (syncing.length === 0) return null;
  // One source: its own phase is the most specific thing we can say. Several:
  // naming them beats a phase that only describes one of them.
  if (syncing.length === 1) {
    const only = syncing[0];
    return only.sync_phase ?? `Syncing ${LABELS[only.kind] ?? only.kind}`;
  }
  const names = syncing.map((c) => LABELS[c.kind] ?? c.kind);
  return `Syncing ${names.slice(0, -1).join(", ")} and ${names[names.length - 1]}`;
}
